import { backward_compatibility, edit, save } from './BlockEditor.js';
import { SmartfireIcon } from './SmartfireIcon.js';
import { createElement } from 'react';
import { toKebabCase } from './utils.js';
export const defaultBlockIcon = createElement(SmartfireIcon, { color: 'unset' });
/**
 *
 * @param smartProps {Map<string, smartProps>}
 * @param configuration {BlockConfiguration}
 * @returns {*}
 */
function __mapSmartPropsToWordpressProps(smartProps, configuration) {
    const attributes = { ...((configuration ?? {}).attributes ?? {}) };
    Object.keys(smartProps).forEach((property) => {
        const prop = smartProps[property];
        if (prop) {
            attributes[property] = {
                type: prop.type,
                default: prop.default,
            };
        }
    });
    return attributes;
}
function __mapDefaultExample(smartProps, exampleConfig) {
    if (exampleConfig === 'disable') {
        return undefined;
    }
    // noinspection SuspiciousTypeOfGuard because of JS, we need to ensure of that value
    if (typeof exampleConfig === 'string') {
        throw new Error(`exampleConfig (${exampleConfig}) is not 'disable'`);
    }
    const { innerBlocks, ...definedExamples } = exampleConfig ?? { attributes: {} };
    const example = {
        attributes: {},
        innerBlocks: [],
    };
    Object.keys(smartProps).forEach(propName => {
        const prop = smartProps[propName];
        switch (prop?.kind) {
            // NOTE: Unsure whether it should read data from attributes or other member
            case 'generic':
                const definedAttributes = definedExamples.attributes;
                example.attributes[propName] = (definedAttributes[propName]) ?
                    definedAttributes[propName] :
                    prop.default;
                break;
            case 'image':
                example.attributes[propName] = {
                    url: `https://picsum.photos/${prop.imageSetup ? prop.imageSetup.width : 200}/${prop.imageSetup ? prop.imageSetup.height : 300}`,
                };
                break;
            case 'picto':
                const randomInt = Math.floor(Math.random() * Math.floor(300));
                example.attributes[propName] = {
                    url: 'https://www.flaticon.com/svg/static/icons/svg/149/149' +
                        randomInt +
                        '.svg',
                };
        }
    });
    return example;
}
/**
 * Transform a React component to a block usable by Smartfire.
 *
 * Smartfire handles any React component, but some metadata can be
 * required to use in conjunction with tools like Gutenberg Editor.
 *
 * This methods handles your specified setup configuration, if a
 * required setting is not provided a default one will be provided.
 *
 * @param setup {React.Component|*} The React component to setup
 * @param smartProps {Map<string, smartProps>} Properties setup for the component, if you use editor update,
 *                                             you have to register your properties
 * @param configuration {BlockConfiguration} Configuration to pass on editor
 * @returns {React.Component}
 */
export default function defineBlock({ Block: component, smartProps = {}, setup = {}, }) {
    let smartComponent = Object.assign(component, {
        smartProps,
        _smartfire: {
            setup,
            async configureGutenberg(name, vendor = "smartfire") {
                const attributes = __mapSmartPropsToWordpressProps(smartProps, setup);
                const example = __mapDefaultExample(smartProps, setup.example);
                if (setup.title === undefined) {
                    setup.title = component.name;
                }
                return {
                    apiVersion: 2,
                    keywords: [],
                    category: vendor,
                    name: vendor + '/' + toKebabCase(name),
                    title: setup.title ?? component.name,
                    icon: setup.icon ?? { src: defaultBlockIcon },
                    ...setup,
                    example,
                    attributes,
                    supports: {
                        ...(setup.supports ?? {}),
                    },
                    deprecated: [
                        {
                            attributes,
                            save: save(smartComponent, name),
                        },
                        ...(await backward_compatibility()).map(b => ({
                            attributes,
                            save: b.save(smartComponent, name),
                        })),
                        // NOTE: Deprecation has 'any' type since the type is not specified
                        ...(setup?.deprecated ?? []).map((depreciation) => {
                            if (depreciation.save) {
                                return depreciation;
                            }
                            else {
                                return { ...depreciation, save: save(smartComponent, name) };
                            }
                        }),
                    ],
                    transforms: setup?.transforms,
                    // Those methods are not overridable
                    edit: await edit(smartComponent),
                    save: await save(smartComponent, name),
                };
            },
        },
    });
    return smartComponent;
}

//# sourceMappingURL=defineBlock.js.map
